/**
 * StatusDetail — Slide-over panel showing individual items in a specific status
 */

import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import type { WasserijItem } from '../../stores/wasserij-store';

const SF_BASE = 'https://cinco.lightning.force.com';

interface StatusDetailProps {
  status: string;
  items: WasserijItem[];
  isLoading: boolean;
  onClose: () => void;
}

function BehandelingPills({ behandelingen }: { behandelingen: string }) {
  if (!behandelingen) return null;
  const pills = behandelingen.split(';').map(b => b.trim()).filter(Boolean);
  if (pills.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1 mt-2">
      {pills.map(pill => (
        <span
          key={pill}
          className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[11px] font-medium border border-indigo-100"
        >
          {pill}
        </span>
      ))}
    </div>
  );
}

export function StatusDetail({ status, items, isLoading, onClose }: StatusDetailProps) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" />

      {/* Panel */}
      <div
        className="relative w-full max-w-lg bg-white shadow-xl flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-main">{status}</h2>
            <p className="text-sm text-muted">{items.length} items</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-muted text-center py-12">Geen items in deze status</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {items.map(item => (
                <div key={item.id} className="px-6 py-4 hover:bg-gray-50">
                  {/* Top row: name + m² */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-main">{item.name}</p>
                        {/* SF link */}
                        {item.sfUrl && (
                          <a
                            href={`${SF_BASE}${item.sfUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-slate-400 hover:text-indigo-500 transition-colors flex-shrink-0"
                            title="Open in Salesforce"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        )}
                      </div>
                      <p className="text-sm text-muted">{item.item}</p>
                    </div>
                    {item.oppervlakteM2 > 0 && (
                      <span className="text-sm font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded flex-shrink-0">
                        {item.oppervlakteM2.toFixed(1)} m²
                      </span>
                    )}
                  </div>

                  {/* Info row */}
                  <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
                    {item.klantNaam && <span>{item.klantNaam}</span>}
                    {item.werkOrderName && (
                      item.sfWerkOrderUrl ? (
                        <a
                          href={`${SF_BASE}${item.sfWerkOrderUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-500 hover:underline"
                        >
                          {item.werkOrderName}
                        </a>
                      ) : (
                        <span>{item.werkOrderName}</span>
                      )
                    )}
                    {item.lengte > 0 && item.breedte > 0 && (
                      <span>{item.lengte} x {item.breedte} cm</span>
                    )}
                    {item.ophaalDatum && (
                      <span>Opgehaald op: {format(parseISO(item.ophaalDatum), 'd MMM', { locale: nl })}</span>
                    )}
                    {/* Photo link */}
                    {item.fotoUrl && (
                      <a
                        href={item.fotoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-500 hover:underline flex items-center gap-1"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Foto's
                      </a>
                    )}
                  </div>

                  {/* Extra behandelingen pills */}
                  {item.extraBehandelingen && (
                    <BehandelingPills behandelingen={item.extraBehandelingen} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
